import React, { useEffect, useMemo, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    createBrandOpsCalendarItem,
    fetchBrandOpsCalendar,
    fetchBrandOpsUgcAssets,
    publishBrandOpsCalendarItem,
} from '../../../redux/brandOps/actions';
import { brandOpsUgcAssetsSelector, brandOpsFetchingUgcAssetsSelector } from '../../../redux/brandOps/selectors';
import type { IBrandOpsUgcAsset } from '../../../redux/brandOps/types';
import type { InstagramComposeResult } from './composeShared';
import {
    BRAND_LUXE_MUTED,
    BRAND_MODAL_ACCENT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_LUXE_PRIMARY,
} from '../brandDashboardTokens';
import { ROUTES } from '../../../types/global/routes.types';
import { BrandManageGate, ManagePageHeader, ManageSurface, MANAGE_PAGE_SX, useBrandManageGate } from './manageShared';
import InstagramComposeModal from './InstagramComposeModal';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';

// ── Color palette (Stitch-aligned) ───────────────────────────────────────────
const C_TEAL = '#00fdc1';
const C_AMBER = '#ffc965';
const C_PURPLE = '#a78bfa';
const C_RED = '#ff6b6b';
const C_ORANGE = '#ffb347';

// ── License expiry helpers ────────────────────────────────────────────────────

function licenseStatus(asset: IBrandOpsUgcAsset): {
    label: string;
    sublabel?: string;
    daysLeft: number | null;
    color: string;
    urgent: boolean;
} {
    if (!asset.exclusive_rights) {
        return { label: 'Perpetual', sublabel: 'Non-exclusive', daysLeft: null, color: C_TEAL, urgent: false };
    }
    const expiresAt = asset.license_expires_at;
    if (!expiresAt)
        return {
            label: 'Exclusive',
            sublabel: 'No expiry set',
            daysLeft: null,
            color: BRAND_LUXE_MUTED,
            urgent: false,
        };
    const msLeft = new Date(expiresAt).getTime() - Date.now();
    const daysLeft = Math.floor(msLeft / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: 'Expired', sublabel: 'Rights ended', daysLeft, color: C_RED, urgent: true };
    if (daysLeft < 30)
        return { label: `${daysLeft}d left`, sublabel: 'Expires soon', daysLeft, color: C_RED, urgent: true };
    if (daysLeft < 90)
        return { label: `${daysLeft}d left`, sublabel: 'Exclusive', daysLeft, color: C_ORANGE, urgent: true };
    if (daysLeft < 180)
        return {
            label: `~${Math.round(daysLeft / 30)}mo`,
            sublabel: 'Exclusive',
            daysLeft,
            color: BRAND_LUXE_PRIMARY,
            urgent: false,
        };
    const months = Math.round(daysLeft / 30);
    return { label: `~${months}mo`, sublabel: 'Exclusive', daysLeft, color: C_TEAL, urgent: false };
}

// Simple deterministic color per campaign ID for accent borders
const CAMPAIGN_ACCENTS = [C_AMBER, C_TEAL, C_PURPLE, '#60a5fa', '#f472b6', '#34d399'];
function campaignAccentColor(id?: string | null): string {
    if (!id) return C_AMBER;
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return CAMPAIGN_ACCENTS[h % CAMPAIGN_ACCENTS.length];
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
    label,
    icon,
    iconColor,
    value,
    valueColor,
    children,
    accentColor,
    borderAlert,
}: {
    label: string;
    icon: React.ReactNode;
    iconColor: string;
    value: React.ReactNode;
    valueColor: string;
    children?: React.ReactNode;
    accentColor: string;
    borderAlert?: boolean;
}) {
    return (
        <Box
            sx={{
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                border: `1px solid ${borderAlert ? `${accentColor}44` : 'rgba(255,255,255,0.07)'}`,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}>
            {/* Top accent line */}
            <Box sx={{ height: 2, background: `linear-gradient(90deg, ${accentColor} 0%, transparent 100%)` }} />

            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                {/* Header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography
                        sx={{
                            color: BRAND_LUXE_MUTED,
                            fontSize: 10.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.09em',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        {label}
                    </Typography>
                    {/* Icon badge */}
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            bgcolor: `${iconColor}18`,
                            border: `1px solid ${iconColor}2a`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <Box sx={{ color: iconColor, display: 'flex', fontSize: 0 }}>{icon}</Box>
                    </Box>
                </Box>

                {/* Main value */}
                <Typography
                    sx={{
                        color: valueColor,
                        fontSize: 30,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        lineHeight: 1,
                        letterSpacing: '-0.5px',
                    }}>
                    {value}
                </Typography>

                {/* Slot for sub-content */}
                {children}
            </Box>
        </Box>
    );
}

// ── Progress bar ─────────────────────────────────────────────────────────────

function GradientBar({ pct, color }: { pct: number; color: string }) {
    return (
        <Box sx={{ height: 3, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <Box
                sx={{
                    height: '100%',
                    width: `${Math.max(0, Math.min(100, pct))}%`,
                    background: `linear-gradient(90deg, ${color}cc 0%, ${color} 100%)`,
                    borderRadius: 4,
                    transition: 'width 0.7s cubic-bezier(0.4,0,0.2,1)',
                }}
            />
        </Box>
    );
}

// ── Asset card ────────────────────────────────────────────────────────────────

function AssetCard({
    asset,
    onSchedule,
}: {
    asset: IBrandOpsUgcAsset;
    onSchedule: (asset: IBrandOpsUgcAsset) => void;
}) {
    const [hovered, setHovered] = useState(false);
    const isVideo =
        asset.media_type === 'video' || Boolean(String(asset.file_key || '').match(/\.(mp4|mov|webm|hls|m3u8)/i));
    const lic = licenseStatus(asset);
    const accent = campaignAccentColor(asset.campaign_id);

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                border: `1px solid ${hovered ? `${accent}44` : 'rgba(255,255,255,0.07)'}`,
                display: 'flex',
                flexDirection: 'column',
                transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.18s',
                boxShadow: hovered
                    ? `0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px ${accent}22`
                    : '0 2px 8px rgba(0,0,0,0.22)',
                transform: hovered ? 'translateY(-3px)' : 'none',
                cursor: 'pointer',
            }}>
            {/* Campaign accent line */}
            <Box sx={{ height: 2, background: `linear-gradient(90deg, ${accent} 0%, transparent 80%)` }} />

            {/* Thumbnail */}
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '4 / 5',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: BRAND_SURFACE_CONTAINER,
                }}>
                {asset.preview_url ? (
                    <Box
                        component="img"
                        src={asset.preview_url}
                        alt={asset.title || 'UGC asset'}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            transition: 'transform 0.4s ease',
                            transform: hovered ? 'scale(1.05)' : 'scale(1)',
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: BRAND_SURFACE_CONTAINER,
                        }}>
                        <PhotoLibraryOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.09)' }} />
                    </Box>
                )}

                {/* Media type chip — top-right */}
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            px: 0.85,
                            py: 0.3,
                            borderRadius: 1,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.13)',
                        }}>
                        {isVideo ? (
                            <PlayCircleOutlineIcon sx={{ fontSize: 11, color: C_TEAL }} />
                        ) : (
                            <ImageOutlinedIcon sx={{ fontSize: 11, color: C_AMBER }} />
                        )}
                        <Typography
                            sx={{
                                color: '#fff',
                                fontSize: 9.5,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                letterSpacing: '0.04em',
                            }}>
                            {isVideo ? 'Video' : 'Image'}
                        </Typography>
                    </Box>
                </Box>

                {/* Deliverable type — top-left (if set) */}
                {asset.deliverable_type && (
                    <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                        <Box
                            sx={{
                                px: 0.85,
                                py: 0.3,
                                borderRadius: 1,
                                bgcolor: `${accent}22`,
                                backdropFilter: 'blur(6px)',
                                border: `1px solid ${accent}44`,
                            }}>
                            <Typography
                                sx={{
                                    color: accent,
                                    fontSize: 9,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                }}>
                                {asset.deliverable_type}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Hover overlay — Schedule CTA */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.72) 100%)',
                        backdropFilter: hovered ? 'blur(1px)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: hovered ? 1 : 0,
                        transition: 'opacity 0.22s',
                        pointerEvents: hovered ? 'auto' : 'none',
                    }}>
                    <Box
                        onClick={() => onSchedule(asset)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${C_AMBER} 0%, ${BRAND_MODAL_ACCENT} 100%)`,
                            color: '#1a1100',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            cursor: 'pointer',
                            boxShadow: `0 4px 16px ${C_AMBER}44`,
                            transition: 'transform 0.12s, box-shadow 0.12s',
                            '&:hover': { transform: 'scale(1.04)', boxShadow: `0 6px 20px ${C_AMBER}66` },
                        }}>
                        <CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />
                        Schedule
                    </Box>
                </Box>

                {/* License urgency banner */}
                {lic.urgent && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: `linear-gradient(0deg, ${lic.color}33 0%, transparent 100%)`,
                            backdropFilter: 'blur(4px)',
                            borderTop: `1px solid ${lic.color}44`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.6,
                        }}>
                        <WarningAmberRoundedIcon sx={{ fontSize: 11, color: lic.color }} />
                        <Typography
                            sx={{
                                color: lic.color,
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                letterSpacing: '0.03em',
                            }}>
                            {lic.label}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Meta */}
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                <Typography
                    sx={{
                        color: '#fff',
                        fontSize: 12.5,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.35,
                    }}>
                    {asset.title || 'Untitled'}
                </Typography>

                {asset.campaign_title && (
                    <Typography
                        sx={{
                            color: BRAND_LUXE_MUTED,
                            fontSize: 10.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                        }}>
                        <Box
                            component="span"
                            sx={{
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                bgcolor: accent,
                                flexShrink: 0,
                                display: 'inline-block',
                            }}
                        />
                        {asset.campaign_title}
                    </Typography>
                )}

                {/* License row */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 0.25,
                        pt: 0.75,
                        borderTop: '1px solid rgba(255,255,255,0.05)',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                            sx={{
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                bgcolor: lic.color,
                                boxShadow: `0 0 5px ${lic.color}`,
                                flexShrink: 0,
                            }}
                        />
                        <Box>
                            <Typography
                                sx={{
                                    color: lic.color,
                                    fontSize: 10.5,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    lineHeight: 1.2,
                                }}>
                                {lic.label}
                            </Typography>
                            {lic.sublabel && (
                                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 9.5, lineHeight: 1.2 }}>
                                    {lic.sublabel}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box
                        onClick={() => onSchedule(asset)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            px: 1,
                            py: 0.35,
                            borderRadius: 1.5,
                            border: `1px solid ${accent}44`,
                            color: accent,
                            bgcolor: `${accent}0d`,
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            cursor: 'pointer',
                            transition: 'background 0.15s, border-color 0.15s',
                            '&:hover': { bgcolor: `${accent}22`, borderColor: `${accent}77` },
                        }}>
                        <CalendarMonthOutlinedIcon sx={{ fontSize: 11 }} />
                        Post
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function AssetCardSkeleton() {
    return (
        <Box
            sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                border: '1px solid rgba(255,255,255,0.06)',
            }}>
            <Box sx={{ height: 2, bgcolor: 'rgba(255,255,255,0.04)' }} />
            <Skeleton
                variant="rectangular"
                sx={{ width: '100%', aspectRatio: '4/5', bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST }}
            />
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Skeleton variant="text" sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, width: '72%' }} />
                <Skeleton
                    variant="text"
                    sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, width: '48%', fontSize: 10 }}
                />
            </Box>
        </Box>
    );
}

// ── Campaign pill tabs ────────────────────────────────────────────────────────

function CampaignPills({
    campaigns,
    value,
    onChange,
}: {
    campaigns: [string, string][];
    value: string;
    onChange: (id: string) => void;
}) {
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
            {[['', 'All'] as [string, string], ...campaigns].map(([id, title]) => {
                const active = value === id;
                const accent = id ? campaignAccentColor(id) : C_AMBER;
                return (
                    <Box
                        key={id || '__all__'}
                        onClick={() => onChange(id)}
                        sx={{
                            px: 1.5,
                            py: 0.55,
                            borderRadius: 2,
                            cursor: 'pointer',
                            border: `1px solid ${active ? `${accent}77` : 'rgba(255,255,255,0.1)'}`,
                            bgcolor: active ? `${accent}18` : 'transparent',
                            transition: 'all 0.15s',
                            '&:hover': {
                                bgcolor: `${accent}12`,
                                borderColor: `${accent}55`,
                            },
                        }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: active ? accent : BRAND_LUXE_MUTED,
                                transition: 'color 0.15s',
                                whiteSpace: 'nowrap',
                            }}>
                            {title}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BrandManageUgcPage() {
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const { brandId } = useBrandManageGate();
    const assets = useSelector(brandOpsUgcAssetsSelector);
    const fetching = useSelector(brandOpsFetchingUgcAssetsSelector);
    const [campaignFilter, setCampaignFilter] = useState<string>('');
    const [scheduleAsset, setScheduleAsset] = useState<IBrandOpsUgcAsset | null>(null);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsUgcAssets({ brandId, force: true }));
    }, [dispatch, brandId]);

    const paidAssets = assets.filter(a => a.is_paid);

    const campaigns = Array.from(
        new Map(
            paidAssets.filter(a => a.campaign_id && a.campaign_title).map(a => [a.campaign_id!, a.campaign_title!]),
        ).entries(),
    );

    const filtered = campaignFilter ? paidAssets.filter(a => a.campaign_id === campaignFilter) : paidAssets;

    const stats = useMemo(() => {
        const videoCount = paidAssets.filter(
            a => a.media_type === 'video' || Boolean(String(a.file_key || '').match(/\.(mp4|mov|webm|hls|m3u8)/i)),
        ).length;
        const imageCount = paidAssets.length - videoCount;
        const exclusiveCount = paidAssets.filter(a => a.exclusive_rights).length;
        const perpetualCount = paidAssets.length - exclusiveCount;
        const creatorCount = new Set(paidAssets.map(a => a.participant_id).filter(Boolean)).size;
        const expiredCount = paidAssets.filter(a => {
            const lic = licenseStatus(a);
            return lic.daysLeft !== null && lic.daysLeft < 0;
        }).length;
        const expiringSoonCount = paidAssets.filter(a => {
            const lic = licenseStatus(a);
            return lic.daysLeft !== null && lic.daysLeft >= 0 && lic.daysLeft < 90;
        }).length;
        const campaignAssetCounts = new Map<string, { title: string; count: number; id: string }>();
        for (const a of paidAssets) {
            if (!a.campaign_id) continue;
            const existing = campaignAssetCounts.get(a.campaign_id);
            if (existing) existing.count++;
            else
                campaignAssetCounts.set(a.campaign_id, {
                    id: a.campaign_id,
                    title: a.campaign_title || a.campaign_id,
                    count: 1,
                });
        }
        const topCampaigns = [...campaignAssetCounts.values()].sort((x, y) => y.count - x.count).slice(0, 4);
        return {
            videoCount,
            imageCount,
            exclusiveCount,
            perpetualCount,
            creatorCount,
            expiredCount,
            expiringSoonCount,
            topCampaigns,
        };
    }, [paidAssets]);

    const onCompose = async (result: InstagramComposeResult) => {
        if (!brandId) return;
        if (!result.publishNow && !result.scheduledAt) {
            showToastMessage('Pick a date and time to schedule', 'error');
            return;
        }
        try {
            const created = (await dispatchP(
                createBrandOpsCalendarItem({
                    brandId,
                    body: {
                        title: result.title || result.caption.slice(0, 48) || `${result.platform} post`,
                        caption: result.caption,
                        description: result.description,
                        hashtags: result.hashtags,
                        scheduled_at: result.scheduledAt ? new Date(result.scheduledAt).toISOString() : undefined,
                        platform: result.platform,
                        format: result.format,
                        aspect_ratio: result.aspect,
                        media_urls: result.mediaUrls,
                        media_type: result.mediaType,
                        platform_options: result.platformOptions,
                        ...(result.projectId ? { project_id: result.projectId } : {}),
                    },
                }),
            )) as any;
            if (result.publishNow && created?._id) {
                await dispatchP(publishBrandOpsCalendarItem({ brandId, itemId: created._id }));
                showToastMessage('Queued for publishing', 'success');
            } else {
                showToastMessage('Added to calendar', 'success');
            }
            dispatch(fetchBrandOpsCalendar({ brandId, force: true }));
            setScheduleAsset(null);
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not create calendar item', 'error');
        }
    };

    const healthColor = stats.expiredCount > 0 ? C_RED : stats.expiringSoonCount > 0 ? C_ORANGE : C_TEAL;

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title="Content Library"
                    subtitle="UGC from your campaigns where payment has been released."
                    back={ROUTES.BRAND_MANAGE}
                />

                {/* Overview panel */}
                {paidAssets.length > 0 && (
                    <Box sx={{ position: 'relative', mb: 2.5 }}>
                        {/* Ambient glow orbs */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -40,
                                left: -40,
                                width: 280,
                                height: 280,
                                borderRadius: '50%',
                                background: `radial-gradient(circle, ${C_AMBER}0d 0%, transparent 70%)`,
                                pointerEvents: 'none',
                                zIndex: 0,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: -40,
                                right: -20,
                                width: 200,
                                height: 200,
                                borderRadius: '50%',
                                background: `radial-gradient(circle, ${C_TEAL}0a 0%, transparent 70%)`,
                                pointerEvents: 'none',
                                zIndex: 0,
                            }}
                        />

                        <Box
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
                                gap: 2,
                            }}>
                            {/* Total Assets */}
                            <StatCard
                                label="Total Assets"
                                icon={<PhotoLibraryOutlinedIcon sx={{ fontSize: 15 }} />}
                                iconColor={C_TEAL}
                                value={paidAssets.length}
                                valueColor={C_TEAL}
                                accentColor={C_TEAL}>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                        <PlayCircleOutlineIcon sx={{ fontSize: 12, color: BRAND_LUXE_MUTED }} />
                                        <Typography sx={{ color: '#fff', fontSize: 11.5 }}>
                                            {stats.videoCount}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.18)', fontSize: 11 }}>·</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                        <ImageOutlinedIcon sx={{ fontSize: 12, color: BRAND_LUXE_MUTED }} />
                                        <Typography sx={{ color: '#fff', fontSize: 11.5 }}>
                                            {stats.imageCount}
                                        </Typography>
                                    </Box>
                                </Box>
                                <GradientBar
                                    pct={paidAssets.length ? (stats.videoCount / paidAssets.length) * 100 : 0}
                                    color={C_TEAL}
                                />
                                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 10 }}>
                                    {paidAssets.length ? Math.round((stats.videoCount / paidAssets.length) * 100) : 0}%
                                    video
                                </Typography>
                            </StatCard>

                            {/* Campaigns */}
                            <StatCard
                                label="Campaigns"
                                icon={<CampaignOutlinedIcon sx={{ fontSize: 15 }} />}
                                iconColor={C_AMBER}
                                value={campaigns.length}
                                valueColor="#fff"
                                accentColor={C_AMBER}>
                                {stats.creatorCount > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PeopleAltOutlinedIcon sx={{ fontSize: 13, color: BRAND_LUXE_MUTED }} />
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11.5 }}>
                                            {stats.creatorCount} creator{stats.creatorCount !== 1 ? 's' : ''}
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7, mt: 0.25 }}>
                                    {stats.topCampaigns.map(c => (
                                        <Box
                                            key={c.id}
                                            onClick={() => setCampaignFilter(c.id)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                '&:hover .cname': { color: campaignAccentColor(c.id) },
                                            }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        bgcolor: campaignAccentColor(c.id),
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    className="cname"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.65)',
                                                        fontSize: 10.5,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        transition: 'color 0.15s',
                                                    }}>
                                                    {c.title}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{ color: BRAND_LUXE_MUTED, fontSize: 10, flexShrink: 0, ml: 1 }}>
                                                {c.count}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </StatCard>

                            {/* License Rights */}
                            <StatCard
                                label="License Rights"
                                icon={<VerifiedOutlinedIcon sx={{ fontSize: 15 }} />}
                                iconColor={C_PURPLE}
                                value={
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                                        <Box>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: C_PURPLE,
                                                    fontSize: 30,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    lineHeight: 1,
                                                }}>
                                                {stats.exclusiveCount}
                                            </Typography>
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 9.5, mt: 0.2 }}>
                                                Exclusive
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: 30,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    lineHeight: 1,
                                                }}>
                                                {stats.perpetualCount}
                                            </Typography>
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 9.5, mt: 0.2 }}>
                                                Perpetual
                                            </Typography>
                                        </Box>
                                    </Box>
                                }
                                valueColor={C_PURPLE}
                                accentColor={C_PURPLE}>
                                {stats.exclusiveCount > 0 && (
                                    <>
                                        <GradientBar
                                            pct={(stats.exclusiveCount / paidAssets.length) * 100}
                                            color={C_PURPLE}
                                        />
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 10 }}>
                                            Exclusive rights · 12-month window
                                        </Typography>
                                    </>
                                )}
                            </StatCard>

                            {/* License Health */}
                            <StatCard
                                label="License Health"
                                icon={
                                    stats.expiredCount > 0 ? (
                                        <ErrorOutlineIcon sx={{ fontSize: 15 }} />
                                    ) : stats.expiringSoonCount > 0 ? (
                                        <WarningAmberRoundedIcon sx={{ fontSize: 15 }} />
                                    ) : (
                                        <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                                    )
                                }
                                iconColor={healthColor}
                                value={
                                    stats.expiredCount === 0 && stats.expiringSoonCount === 0 ? (
                                        'All clear'
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                                            {stats.expiredCount > 0 && (
                                                <Box>
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            color: C_RED,
                                                            fontSize: 30,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            lineHeight: 1,
                                                        }}>
                                                        {stats.expiredCount}
                                                    </Typography>
                                                    <Typography
                                                        sx={{ color: BRAND_LUXE_MUTED, fontSize: 9.5, mt: 0.2 }}>
                                                        Expired
                                                    </Typography>
                                                </Box>
                                            )}
                                            {stats.expiringSoonCount > 0 && (
                                                <Box>
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            color: C_ORANGE,
                                                            fontSize: 30,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            lineHeight: 1,
                                                        }}>
                                                        {stats.expiringSoonCount}
                                                    </Typography>
                                                    <Typography
                                                        sx={{ color: BRAND_LUXE_MUTED, fontSize: 9.5, mt: 0.2 }}>
                                                        Expiring
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )
                                }
                                valueColor={healthColor}
                                accentColor={healthColor}
                                borderAlert={stats.expiredCount > 0 || stats.expiringSoonCount > 0}>
                                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 10.5, lineHeight: 1.45 }}>
                                    {stats.expiredCount === 0 && stats.expiringSoonCount === 0
                                        ? 'No exclusive licenses expiring in the next 90 days.'
                                        : stats.expiredCount > 0
                                        ? 'Exclusive rights ended. Review or renew usage agreements.'
                                        : 'Schedule these assets before their exclusive window closes.'}
                                </Typography>
                            </StatCard>
                        </Box>
                    </Box>
                )}

                <ManageSurface>
                    {/* Campaign pill filter */}
                    {campaigns.length > 1 && (
                        <CampaignPills campaigns={campaigns} value={campaignFilter} onChange={setCampaignFilter} />
                    )}

                    {fetching && !assets.length ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                                gap: 2,
                            }}>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <AssetCardSkeleton key={i} />
                            ))}
                        </Box>
                    ) : !filtered.length ? (
                        <Box sx={{ textAlign: 'center', py: 12 }}>
                            {/* Glow orb behind icon */}
                            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: -24,
                                        borderRadius: '50%',
                                        background: `radial-gradient(circle, ${C_AMBER}12 0%, transparent 70%)`,
                                        pointerEvents: 'none',
                                    }}
                                />
                                <PhotoLibraryOutlinedIcon
                                    sx={{ fontSize: 48, color: BRAND_LUXE_MUTED, opacity: 0.35, position: 'relative' }}
                                />
                            </Box>
                            <Typography sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, mb: 0.75 }}>
                                No content yet
                            </Typography>
                            <Typography
                                sx={{
                                    color: BRAND_LUXE_MUTED,
                                    fontSize: 13,
                                    maxWidth: 320,
                                    mx: 'auto',
                                    lineHeight: 1.55,
                                }}>
                                Approved and paid submissions from your campaigns will appear here.
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                                gap: 2,
                            }}>
                            {filtered.map(asset => (
                                <AssetCard key={asset._id} asset={asset} onSchedule={setScheduleAsset} />
                            ))}
                        </Box>
                    )}
                </ManageSurface>
            </Box>

            <InstagramComposeModal
                open={Boolean(scheduleAsset)}
                onClose={() => setScheduleAsset(null)}
                onSubmit={onCompose}
                initialAsset={
                    scheduleAsset
                        ? {
                              file_key: scheduleAsset.file_key,
                              preview_url: scheduleAsset.preview_url,
                              title: scheduleAsset.title,
                              project_id: scheduleAsset.project_id,
                          }
                        : null
                }
            />
        </BrandManageGate>
    );
}
